import { ComputersAndVirtualMachinesView } from '../view/ComputersAndVirtualMachinesView';
import { templateComputersAndVirtualMachines } from '../view/template';
import { ComputersAndVirtualMachinesRepository } from '../repository/ComputersAndVirtualMachinesRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { ComputersAndVirtualMachinesWebComponent } from '..';
import {
  ComputersAndVirtualMachinesViewData,
  ComputersAndVmItem,
  RestoreFormData,
  Column,
  ColumnKeys,
  Dependencies,
  MAX_FILE_SIZE,
  PendingTypes,
  RetrieveQuarantineFilePayload,
  QuarantineActionStatus,
  SubmitToLabsFilePayload,
  SubmitToLabsDialogContent,
} from '../types';
import { GzDialogEvents } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from 'apps/webcomponents/src/ui/gzDialog';
import { AddExclusionsDialog } from '../../uiElements/dialogs/addExclusionDialog';
import { GridViewController } from 'apps/webcomponents/src/grid/gridView/controller/GridViewController';
import { RestoreWindow } from '../../uiElements/dialogs/restoreWindow';
import { GridEvents } from 'apps/webcomponents/src/grid/types/common';
import { QuarantineActionEnabler } from '../helpers/quarantineActionsEnabler';
import { QuarantineToastNotification } from '../../uiElements/notifications/toastNotification';
import { EmptyQuarantineDialog } from '../../uiElements/dialogs/emptyQuarantineDialog';
import { DeleteDialog } from '../../uiElements/dialogs/deleteDialog';
import * as FileSaver from 'file-saver';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { QuarantineComputersAndVirtualMachinesRestoreForm } from '../../uiElements/quarantineComputersAndVirtualMachinesRestoreForm/component/QuarantineComputersAndVirtualMachinesRestoreForm';
import { DownloadDialog } from '../../uiElements/dialogs/downloadWindow';
import { translateFn } from '../../../utils/translator.helper';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { ComputersAndVirtualMachinesGridEvents } from '../events/events';
import { SubmitToLabsDialog } from '../../uiElements/dialogs/submitToLabsDialog';
import { PRODUCT_ID_TO_NAME } from '@nirvana/shared/license-helper';
import { ReadLicenseUsageDetailsPayload } from '../../../utils/request';
import { ReadLicenseUsageDetailsResponse } from '../../../utils/response';
import { SubmitToLabsWebComponent } from '../../uiElements/submitToLabs';

export class ComputersAndVirtualMachinesController extends BaseWebComponentController {
  public component: ComputersAndVirtualMachinesWebComponent;
  public dataRepository: ComputersAndVirtualMachinesRepository;
  public view: ComputersAndVirtualMachinesView;
  public viewConstructor: typeof ComputersAndVirtualMachinesView;
  public dataRepositoryConstructor: typeof ComputersAndVirtualMachinesRepository;
  public dependencies: Dependencies;

  constructor(component: ComputersAndVirtualMachinesWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: ComputersAndVirtualMachinesView,
      viewTemplate: templateComputersAndVirtualMachines,
      dataRepositoryConstructor: ComputersAndVirtualMachinesRepository,
    });
  }

  public async getDataForView(): Promise<ComputersAndVirtualMachinesViewData> {
    return this.dataRepository.getDataForView(this.component.externalFilters);
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$restoreBtn,
        event: 'click',
        callback: () => this.openRestoreDialog(),
      },
      {
        element: this.view.dom.$retrieveBtn,
        event: ButtonEvents.CLICK,
        callback: () => this.openDownloadDialog(),
      },
      {
        element: this.view.dom.$emptyQuarantineBtn,
        event: 'click',
        callback: () => this.openEmptyQuarantineDialog(),
      },
      {
        element: this.view.dom.$deleteTaskBtn,
        event: 'click',
        callback: () => this.openDeleteDialog(),
      },
      {
        element: this.view.dom.$downloadBtn,
        event: 'click',
        callback: () => this.downloadSelectedItems(),
      },
      {
        element: this.view.dom.$addExclusionsBtn,
        event: 'click',
        callback: () => this.openAddExclusionsDialog(),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: ({ detail: { selectedItemsData } }) => this.enableDisableGridActions(selectedItemsData),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROWS_LOADING_FINISHED,
        callback: ({ detail }) => this.onRowsLoadingFinished(detail),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EXPORT_CSV,
        callback: () => this.exportCsv(),
      },
      {
        element: this.view.dom.$grid,
        event: ComputersAndVirtualMachinesGridEvents.OnSubmitToSandboxQuarantineFile,
        callback: this.submitToSandboxRetrievedQuarantineFile,
      },
      {
        element: this.view.dom.$grid,
        event: ComputersAndVirtualMachinesGridEvents.OnSubmitToLabsQuarantineFile,
        callback: this.submitToLabsRetrievedQuarantineFile,
      },
      {
        element: this.view.dom.$grid,
        event: ComputersAndVirtualMachinesGridEvents.OnDownloadRetrievedQuarantineFile,
        callback: this.downloadRetrievedQuarantineFile,
      },
      {
        element: this.view.dom.$grid,
        event: ComputersAndVirtualMachinesGridEvents.OnDeletedRetrievedQuarantineFile,
        callback: this.deleteRetrievedQuarantineFile,
      },
    ];
  }

  private openRestoreDialog(): void {
    const { selectedItems: records } = this.view.dom.$grid as { selectedItems: Array<ComputersAndVmItem> };
    let itemsFromArchive = false;
    records.forEach(record => {
      if (record.source.filePath.indexOf('=>') !== -1) {
        itemsFromArchive = true;
      }
    });
    const quarantineRestoreForm = new QuarantineComputersAndVirtualMachinesRestoreForm();
    quarantineRestoreForm.translateFn = this.dependencies.translateFn;
    if (itemsFromArchive) {
      quarantineRestoreForm.itemsFromArchive = itemsFromArchive;
    }

    const dialog = RestoreWindow.open(quarantineRestoreForm, this.dependencies.translateFn, this.view.dom.$hostElement);

    this.attachListeners(dialog, this.restoreQuarantinedItems);
  }

  private openDeleteDialog(): void {
    const { selectedItems } = this.view.dom.$grid as { selectedItems: Array<ComputersAndVmItem> };
    const hasGhostItems = selectedItems?.some(item => item.isGhost);

    const dialog = DeleteDialog.open(this.dependencies.translateFn, hasGhostItems);

    this.attachListeners(dialog, this.deleteEntries);
  }

  private async downloadSelectedItems(): Promise<void> {
    const { selectedItems } = this.view.dom.$grid as { selectedItems: Array<ComputersAndVmItem> };
    const downloadIds = selectedItems
      .filter(item => !item?.source?.protectedEntity?.sve?.sveMultiPlatform)
      .map(item => item._id)
      .join('|');

    if (!downloadIds.length) {
      return;
    }

    try {
      const { response, fileName } = await this.dataRepository.downloadSelected(downloadIds);

      FileSaver.saveAs(response, fileName);
    } catch (e) {
      console.error(e);
    }
  }

  private retrieveQuarantineFile = async (dialog: GzDialogWebComponent, event: WebComponentEvent): Promise<void> => {
    const { selectedItems } = this.view.dom.$grid as { selectedItems: Array<ComputersAndVmItem> };

    dialog.close();

    if (selectedItems.length !== 1) {
      return;
    }
    const payload: RetrieveQuarantineFilePayload = {
      records: selectedItems,
      archiveName: event.detail.filename,
      archivePassword: event.detail.password,
    };

    try {
      await this.dataRepository.retrieveQuarantineFile(payload);
      this.reloadGridItems();
      this.openCreateTaskToast();
    } catch (e: any) {
      this.reloadGridItems();
      this.showToast(ToastSeverity.ERROR, translateFn('Retrieve task failed.'));
    }
  };

  private showToast(severity: any, message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message,
      severity: severity,
    });
  }

  private deleteEntries = async (dialog: GzDialogWebComponent): Promise<void> => {
    const { selectedItems: records } = this.view.dom.$grid as { selectedItems: Array<ComputersAndVmItem> };

    dialog.close();

    if (records) {
      try {
        const { nbOfCreatedTasks } = await this.dataRepository.deleteSelected({ records });

        if (nbOfCreatedTasks) {
          this.openCreateTaskToast();
        }
      } catch (e) {
        this.showToast(ToastSeverity.ERROR, translateFn('Delete task failed.'));
      }

      this.reloadGridItems();
    }
  };

  private openEmptyQuarantineDialog(): void {
    const { translateFn, permissions } = this.dependencies;
    const dialog = EmptyQuarantineDialog.open(translateFn, permissions);

    this.attachListeners(dialog, this.emptyQuarantine);
  }

  private emptyQuarantine = async (dialog: GzDialogWebComponent, { detail }): Promise<void> => {
    dialog.close();

    try {
      const { nbOfCreatedTasks } = await this.dataRepository.emptyQuarantine(detail);

      if (nbOfCreatedTasks) {
        this.openCreateTaskToast();
      }
    } catch (e) {}

    this.reloadGridItems();
  };

  private restoreQuarantinedItems = async (dialog: GzDialogWebComponent, { detail }): Promise<void> => {
    const { restorePath, restoreType } = detail as RestoreFormData;
    const { selectedItems: records } = this.view.dom.$grid as { selectedItems: Array<ComputersAndVmItem> };

    dialog.close();

    if (records?.length) {
      try {
        await this.dataRepository.restoreQuarantine({
          addExclusion: false,
          edr: null,
          records,
          restorePath,
          restoreType,
        });

        this.reloadGridItems();
        this.openCreateTaskToast();
      } catch (e) {
        this.reloadGridItems();
        this.showToast(ToastSeverity.ERROR, translateFn('Restore task failed.'));
      }
    }
  };

  private openCreateTaskToast() {
    QuarantineToastNotification.createToastNotification({
      message: this.dependencies.translateFn('Task created.'),
      actionButtonText: this.dependencies.translateFn('View status'),
      hasAction: true,
      actionCallback: this.dependencies.navigateToTasks,
    });
  }

  private openAddExclusionsDialog(): void {
    const { selectedItems } = this.view.dom.$grid as { selectedItems: Array<ComputersAndVmItem> };
    const selectedItemCount = selectedItems?.length || 0;
    const dialog = AddExclusionsDialog.open(this.dependencies.translateFn, selectedItemCount);

    this.attachListeners(dialog, () => this.addExclusions(dialog, selectedItems!));
  }

  private addExclusions = (dialog: GzDialogWebComponent, selectedItems: Array<ComputersAndVmItem>) => {
    if (selectedItems.length) {
      this.dataRepository.addExclusion(selectedItems).then(response => {
        if (!response.success) {
          GzToastNotificationWebComponent.buildToastWrapperElement({
            message: this.dependencies.translateFn('Could not add exclusions.'),
            severity: ToastSeverity.ERROR,
            x: ToastPositionX.RIGHT,
            y: ToastPositionY.BOTTOM,
          });
        } else {
          //show configuration profiles page

          return this.dependencies.navigateToConfigurationProfiles();
        }
      });
    }

    dialog.close();
  };

  private async reloadGridItems(): Promise<void> {
    await (this.view.dom.$grid?.controller as GridViewController<Column, ColumnKeys>)?.handleGridItemsLoad();
  }

  private attachListeners(dialog: GzDialogWebComponent, confirmCallback: Function) {
    dialog.addEventListener(GzDialogEvents.CONFIRM, event => confirmCallback(dialog, event), { once: true });
    dialog.addEventListener(GzDialogEvents.CANCEL, () => dialog.close(), { once: true });
  }

  private async enableDisableGridActions(selectedItems: Array<ComputersAndVmItem>): Promise<void> {
    await this.validateRetrieveAction(selectedItems);
    QuarantineActionEnabler.handleActionButtons(this.view, selectedItems);
  }

  private async validateRetrieveAction(selectedItems: Array<ComputersAndVmItem>): Promise<void> {
    this.view.setTooltipRetrieve('');
    this.view.enableRetrieveButton(false);
    if (!Application.Main.product.isCloud()) {
      return;
    }

    const selectedLength = selectedItems.length;
    if (selectedLength !== 1) {
      if (selectedLength > 1) {
        this.view.setTooltipRetrieve(translateFn('You can retrieve only one file at a time.'));
      }
      return;
    }

    const selectedItem = selectedItems.at(0);
    if (!selectedItem) {
      return;
    }

    if (!QuarantineActionEnabler.userHasDownloadQuarantineRights()) {
      this.view.setTooltipRetrieve(
        translateFn('You need Manage Networks and Manage Company rights to retrieve files.')
      );
      return;
    }

    if (PendingTypes.includes(selectedItem?.actionStatus)) {
      this.view.setTooltipRetrieve(translateFn('Another task is already in progress or pending.'));
      return;
    }

    if (selectedItem.actionStatus === QuarantineActionStatus.ACTION_STATUS_SUCCEEDED_RETRIEVE) {
      this.view.setTooltipRetrieve(translateFn('The selected file was already retrieved.'));
      return;
    }

    let isBelowLimit = false;
    if (selectedItem.size) {
      isBelowLimit = selectedItem.size < MAX_FILE_SIZE;
      if (!isBelowLimit) {
        this.view.setTooltipRetrieve(translateFn('The file size exceeds the allowed limit of 25 MB.'));
        return;
      }
    }
    try {
      await this.dataRepository.validateRetrieveAction({ endpointId: selectedItem.source.endpointId });
      this.view.enableRetrieveButton(true);
    } catch (e: any) {
      this.view.setTooltipErrorCodesTranslated(e);
    }
  }

  private async onRowsLoadingFinished(detail: { loadedItems: number }): Promise<void> {
    this.updateEmptyQuarantineButtonState(detail);
  }

  private updateEmptyQuarantineButtonState(detail: { loadedItems: number }): void {
    const { loadedItems } = detail;

    if (loadedItems) {
      this.view.enableEmptyQuarantine(true);
    }
  }

  private async exportCsv(): Promise<void> {
    const { selectedItems } = this.view.dom.$grid;

    if (selectedItems) {
      // Export CSV
    }
  }

  /**
   * Start download retrieved quarantine archive
   *
   * @param evt
   * @private
   */
  private async downloadRetrievedQuarantineFile(evt: CustomEvent): Promise<void> {
    try {
      await this.dataRepository.validateRetrievedFile({ fileId: evt.detail.fileId });
      Application.lib.DownloadManager.download('/storage/quarantine/' + evt.detail.fileId);
    } catch (e) {
      this.showToast(ToastSeverity.ERROR, translateFn('Failed to download retrieved file.'));
    }
  }

  /**
   * Delete quarantine archive
   *
   * @param evt
   * @private
   */
  private async deleteRetrievedQuarantineFile(evt: CustomEvent): Promise<void> {
    try {
      await this.dataRepository.deleteRetrievedQuarantineFile({
        quarantineId: evt.detail.quarantineId,
      });
      await this.reloadGridItems();
      this.showToast(ToastSeverity.SUCCESS, translateFn('Retrieved file deleted successfully.'));
    } catch (e: any) {
      this.showToast(ToastSeverity.ERROR, translateFn('Failed to delete retrieved file.'));
    }
  }

  private openDownloadDialog(): void {
    const dialog = DownloadDialog.open(this.view.dom.$hostElement);
    this.attachListeners(dialog, this.retrieveQuarantineFile);
  }

  private async submitToSandboxRetrievedQuarantineFile(evt: CustomEvent): Promise<void> {
    try {
      await this.dataRepository.validateRetrievedFile({ fileId: evt.detail.fileId });
      this.dependencies.navigateToSandbox(evt.detail);
    } catch (e) {
      this.showToast(ToastSeverity.ERROR, translateFn('Failed to submit retrieved file.'));
    }
  }

  /**
   * Gets the callback function needed for attachListeners, as a closure with the quarantineId and SubmitToLabsWebComponent (required for turing on and off the loading animation)
   *
   * @param {string} quarantineId
   * @param {SubmitToLabsWebComponent} component
   * @returns {(dialog: GzDialogWebComponent, event: WebComponentEvent) => Promise<void>}
   * @private
   */
  private submittedToLabs(
    quarantineId: string,
    component: SubmitToLabsWebComponent
  ): (dialog: GzDialogWebComponent, event: WebComponentEvent) => Promise<void> {
    const _submittedToLabs = async (dialog: GzDialogWebComponent, event: WebComponentEvent): Promise<void> => {
      component.setLoadingState();

      const payload: SubmitToLabsFilePayload = {
        productId: event.detail.product,
        fullName: event.detail.fullName,
        email: event.detail.email,
        quarantineId: quarantineId,
        password: event.detail.password,
        sensitiveFiles: event.detail.sensitiveFiles,
      };

      try {
        await this.dataRepository.submitToLabs(payload);
        dialog.close();
        await this.reloadGridItems();
        this.showToast(ToastSeverity.SUCCESS, translateFn('File submitted successfully'));
      } catch (e: any) {
        const errorMsg = this.view.getSubmitToLabsCodesTranslated(e);
        this.showToast(ToastSeverity.ERROR, errorMsg);
        this.attachListeners(dialog, _submittedToLabs);
      } finally {
        component.removeLoadingState();
      }
    };
    return _submittedToLabs;
  }

  private async submitToLabsRetrievedQuarantineFile(evt: CustomEvent): Promise<void> {
    const user: { _id?: string; profile: { fullName?: string }; email?: string } = Application.Main.getUser();

    let products: ReadLicenseUsageDetailsResponse;
    try {
      const payload: ReadLicenseUsageDetailsPayload = {
        returnOnlyCount: false,
        unlicensedEndpointCount: false,
        companyId: this.dependencies.getCurrentUserCompany().id,
        userId: user._id ?? null,
        includeAddons: false,
      };
      products = await this.dataRepository.readLicenseUsageDetails(payload);
    } catch (e) {
      this.showToast(ToastSeverity.ERROR, translateFn('Failed to submit file.'));
      return;
    }

    const data: SubmitToLabsDialogContent = {
      product: products.data.map(product => {
        return {
          title: PRODUCT_ID_TO_NAME[product.productId](),
          value: product.productId,
        };
      }),
      fullName: user.profile.fullName ?? '',
      email: user.email ?? '',
      filename: evt.detail.archiveName,
    };

    const [dialog, component]: [GzDialogWebComponent, SubmitToLabsWebComponent] = SubmitToLabsDialog.open(
      this.view.dom.$hostElement,
      data
    );
    this.attachListeners(dialog, this.submittedToLabs(evt.detail.quarantineId, component));
  }
}
